// The resource-event stream over fetch (spec/api-conventions.md section 21.5; spec/dashboard.md section 2.2): a browser
// EventSource cannot send the bearer, so the dashboard reads `text/event-stream` with fetch, parses the frames itself,
// and reconnects with `Last-Event-ID` after an error with a backoff of 1 s, 2 s, 4 s … up to 30 s (reset by a frame).
// A refusal the server will repeat (400 / 401 / 403 / 404 / 410, or a body that is not an event stream) stops it: the
// page then keeps its poll, which is what `onStateChange` is for.

/** One dispatched frame: `id:` (the stream seq), `event:` (the kind) and `data:` (JSON parsed when it is JSON). */
export interface StreamFrame {
  id: string | null;
  event: string;
  data: unknown;
}

/** connecting → connected; reconnecting while it waits to retry; stopped when aborted or refused for good. */
export type StreamState = 'connecting' | 'connected' | 'reconnecting' | 'stopped';

export interface StreamStateInfo {
  /** the HTTP status of the refusal or failure, when there was a response */
  status?: number;
  /** the transport error, when there was no response */
  error?: unknown;
  /** the delay before the next attempt (reconnecting) */
  retryInMs?: number;
}

export interface OpenStreamOptions {
  url: string;
  /** the bearer source (the client's `getToken`); read on every (re)connect */
  token: () => string | null;
  /** resume after this seq (sent as `Last-Event-ID`) */
  lastEventId?: string | null;
  signal: AbortSignal;
  onFrame: (frame: StreamFrame) => void;
  onStateChange?: (state: StreamState, info?: StreamStateInfo) => void;
  /** a 401: the session is over (the client's onUnauthenticated) */
  onUnauthenticated?: () => void;
  fetchImpl?: typeof fetch;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  /** the wait between attempts (tests pass a fake); resolves early when the signal aborts */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

/** The statuses a reconnect would only repeat: the stream stops and the page keeps polling. */
const PERMANENT = new Set([400, 401, 403, 404, 405, 410]);

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

function newRequestId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The SSE line parser (the WHATWG rules the server's frames need): lines end with LF, CRLF or CR; a blank line
 * dispatches; `:` starts a comment (the keepalive); `id`, `event`, `data` (joined with LF), `retry` ignored.
 */
export class FrameParser {
  private buffer = '';
  private id: string | null = null;
  private event = '';
  private data: string[] = [];
  private sawField = false;
  private afterCR = false;
  /** the last `id:` seen, dispatched or not: the resume point */
  lastEventId: string | null;

  constructor(
    private readonly onFrame: (frame: StreamFrame) => void,
    private readonly onComment: (text: string) => void = () => {},
    lastEventId: string | null = null,
  ) {
    this.lastEventId = lastEventId;
  }

  push(chunk: string): void {
    let text = chunk;
    // a CR ended the previous chunk's last line: an LF opening this chunk is the second half of that CRLF
    if (this.afterCR && text.startsWith('\n')) text = text.slice(1);
    this.afterCR = false;
    this.buffer += text;
    for (;;) {
      const m = /\r\n|\n|\r/.exec(this.buffer);
      if (!m) break;
      const line = this.buffer.slice(0, m.index);
      this.buffer = this.buffer.slice(m.index + m[0].length);
      if (m[0] === '\r' && this.buffer === '') this.afterCR = true;
      this.line(line);
    }
  }

  /** The connection ended: an incomplete frame is discarded (the SSE rule). */
  reset(): void {
    this.buffer = '';
    this.afterCR = false;
    this.event = '';
    this.data = [];
    this.sawField = false;
  }

  private line(line: string): void {
    if (line === '') {
      this.dispatch();
      return;
    }
    if (line.startsWith(':')) {
      this.onComment(line.slice(1).trim());
      return;
    }
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    this.sawField = true;
    if (field === 'data') this.data.push(value);
    else if (field === 'event') this.event = value;
    else if (field === 'id') {
      if (!value.includes('\u0000')) {
        this.id = value;
        this.lastEventId = value;
      }
    }
  }

  private dispatch(): void {
    const had = this.sawField;
    const data = this.data;
    const event = this.event || 'message';
    this.data = [];
    this.event = '';
    this.sawField = false;
    if (!had || data.length === 0) return;
    const text = data.join('\n');
    let parsed: unknown = text;
    try {
      parsed = JSON.parse(text);
    } catch {
      // not JSON: the raw text
    }
    this.onFrame({ id: this.id, event, data: parsed });
  }
}

/**
 * Opens the stream and keeps it open until `signal` aborts or the server refuses it for good. Resolves when it stopped.
 */
export async function openStream(o: OpenStreamOptions): Promise<void> {
  const fetchImpl = o.fetchImpl ?? ((input, init) => fetch(input, init));
  const sleep = o.sleep ?? defaultSleep;
  const initial = o.initialBackoffMs ?? 1000;
  const max = o.maxBackoffMs ?? 30000;
  let delay = initial;
  let state: StreamState | null = null;
  const set = (next: StreamState, info?: StreamStateInfo) => {
    if (next === state && next !== 'reconnecting') return;
    state = next;
    o.onStateChange?.(next, info);
  };
  const parser = new FrameParser(
    (frame) => {
      delay = initial; // a frame proves the connection: the next failure starts the backoff again
      o.onFrame(frame);
    },
    () => {
      delay = initial;
    },
    o.lastEventId ?? null,
  );

  set('connecting');
  while (!o.signal.aborted) {
    let failure: StreamStateInfo = {};
    try {
      const headers: Record<string, string> = { Accept: 'text/event-stream', 'Cache-Control': 'no-cache', 'X-Request-Id': newRequestId() };
      const token = o.token();
      if (token) headers.Authorization = `Bearer ${token}`;
      if (parser.lastEventId) headers['Last-Event-ID'] = parser.lastEventId;
      const response = await fetchImpl(o.url, { method: 'GET', headers, signal: o.signal });
      const type = response.headers.get('Content-Type') ?? '';
      if (!response.ok || !type.toLowerCase().startsWith('text/event-stream')) {
        try {
          await response.body?.cancel();
        } catch {
          // already closed
        }
        if (response.status === 401) o.onUnauthenticated?.();
        if (PERMANENT.has(response.status) || (response.ok && !type.toLowerCase().startsWith('text/event-stream'))) {
          set('stopped', { status: response.status });
          return;
        }
        failure = { status: response.status };
      } else if (!response.body) {
        failure = { status: response.status };
      } else {
        set('connected');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          parser.push(decoder.decode(value, { stream: true }));
        }
        parser.push(decoder.decode());
        parser.reset();
        // a clean end (a restart of the API, a proxy timeout): reconnect from the last id
      }
    } catch (e) {
      if (o.signal.aborted) break;
      parser.reset();
      failure = { error: e };
    }
    if (o.signal.aborted) break;
    set('reconnecting', { ...failure, retryInMs: delay });
    await sleep(delay, o.signal);
    delay = Math.min(delay * 2, max);
  }
  set('stopped');
}
